import { WithLoadingLog } from 'components/general/loaders/Spinner/Spinner';
import React from 'react';
import {
  RouteObject,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import GDRPInfo from 'components/core/GDPRClause/GDRPInfo';
import NotFoundPage from 'components/core/NotFoundPage/NotFoundPage';
import Bouncer from 'components/general/loaders/Bouncing/Bouncer';
import { useAuth0 } from '../../services/auth0/react-auth0.spa';
import { Spinner } from 'components/core';
import UnifiedIssueViewContainer from '../../views/issue/issueView/UnifiedIssueViewContainer';
import PrivateRoute from './PrivateRoute';
// import UnauthorizedScreen from './UnauthorizedScreen';
import { AuthorizableDocumentations } from './routeComponents/documentation';
import { AuthorizableInspection } from './routeComponents/inspection';
import { AuthorizableInspectionWizard } from './routeComponents/inspection';
import { AuthorizableTemplate } from './routeComponents/inspectionTemplate';
import { AuthorizableWorkflowEditor } from './routeComponents/workflow';
import InsufficientPermissionsPage from 'components/core/InsufficientPermissionsPage/InsufficientPermissionsPage';
import { useCurrentUser } from 'store/user';
import { useProjectId } from 'store/project';
import { hasVisibleNoAccessRoleForProject } from 'shared/domain/permissions/projectPermissionsGuards';
import { APP_PATHS } from './paths';
import {
  projectIdParam,
  issueIdParam,
  eventIdParam,
  decisionIdParam,
  documentIdParam,
  linkedDocumentationVersionIdParam,
  inspectionTemplateIdParam,
  checklistItemIdParam,
  directoryIdParam,
  fileIdParam,
  inspectionIdParam,
  protocolIdParam,
  sectionIdParam,
  workflowIdParam,
} from './routeUrlParams';

const ProjectAuthWrapper = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const { isAuthenticated, loading } = useAuth0();
  const location = useLocation();

  // Wait for Auth0 to finish initializing before making routing decisions
  if (loading || isAuthenticated === undefined) {
    return <Bouncer backgroundColor='#ccc' />;
  }

  if (isAuthenticated === false) {
    const redirectLink = `${location.pathname}${location.search}`;
    return <Navigate to='/login' state={{ targetUrl: redirectLink }} />;
  }

  return <>{children}</>;
};

const LegacyRedirectComponent = (): React.ReactElement => {
  const location = useLocation();
  const pathnameArray = location.pathname.split('/');
  const siteIndex = pathnameArray.findIndex((text) => text === 'site');
  if (siteIndex < 0) {
    return <Navigate to={'/issue'} />;
  }
  pathnameArray.splice(siteIndex, 2);
  return <Navigate to={pathnameArray.join('/')} />;
};

function toNestedProjectPath(path: string): string {
  return path.replace('/project/', '');
}

export const getProjectRoutes = (): RouteObject[] => [
  // Legacy redirects
  {
    path: ':projectId/inspection/:inspectionId/protocol/:protocolId/document/:documentId',
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: ':projectId/site/:siteId/issue/:issueId/event/:eventId/document/:documentId',
    element: (
      <ProjectAuthWrapper>
        <LegacyRedirectComponent />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: ':projectId/site/:siteId/issue/:issueId/document/:documentId',
    element: (
      <ProjectAuthWrapper>
        <LegacyRedirectComponent />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: ':projectId/site/:siteId/issue/:issueId/event/:eventId',
    element: (
      <ProjectAuthWrapper>
        <LegacyRedirectComponent />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: ':projectId/site/:siteId/issue/:issueId',
    element: (
      <ProjectAuthWrapper>
        <LegacyRedirectComponent />
      </ProjectAuthWrapper>
    ),
  },
  // Current routes
  {
    path: toNestedProjectPath(
      APP_PATHS.issue.document.onEvent(
        projectIdParam,
        issueIdParam,
        eventIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <React.Suspense
          fallback={
            <WithLoadingLog reason='Route React.Suspense fallback'>
              <Spinner reason='Route React.Suspense fallback' />
            </WithLoadingLog>
          }
        >
          <PrivateRoute element={UnifiedIssueViewContainer} />
        </React.Suspense>
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.issue.document.onIssue(
        projectIdParam,
        issueIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <React.Suspense
          fallback={
            <WithLoadingLog reason='Route React.Suspense fallback'>
              <Spinner reason='Route React.Suspense fallback' />
            </WithLoadingLog>
          }
        >
          <PrivateRoute element={UnifiedIssueViewContainer} />
        </React.Suspense>
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.issue.documentation(
        projectIdParam,
        issueIdParam,
        linkedDocumentationVersionIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <React.Suspense
          fallback={
            <WithLoadingLog reason='Route React.Suspense fallback'>
              <Spinner reason='Route React.Suspense fallback' />
            </WithLoadingLog>
          }
        >
          <PrivateRoute element={UnifiedIssueViewContainer} />
        </React.Suspense>
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.issue.event(projectIdParam, issueIdParam, eventIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <React.Suspense
          fallback={
            <WithLoadingLog reason='Route React.Suspense fallback'>
              <Spinner reason='Route React.Suspense fallback' />
            </WithLoadingLog>
          }
        >
          <PrivateRoute element={UnifiedIssueViewContainer} />
        </React.Suspense>
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.issue.decision(projectIdParam, issueIdParam, decisionIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <React.Suspense
          fallback={
            <WithLoadingLog reason='Route React.Suspense fallback'>
              <Spinner reason='Route React.Suspense fallback' />
            </WithLoadingLog>
          }
        >
          <PrivateRoute element={UnifiedIssueViewContainer} />
        </React.Suspense>
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.issue.single(projectIdParam, issueIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <React.Suspense
          fallback={
            <WithLoadingLog reason='Route React.Suspense fallback'>
              <Spinner reason='Route React.Suspense fallback' />
            </WithLoadingLog>
          }
        >
          <PrivateRoute element={UnifiedIssueViewContainer} />
        </React.Suspense>
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspectionTemplate.document.onGeneralNorm(
        projectIdParam,
        inspectionTemplateIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableTemplate} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspectionTemplate.document.onChecklistItemNorm(
        projectIdParam,
        inspectionTemplateIdParam,
        checklistItemIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableTemplate} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspectionTemplate.document.onChecklistItem(
        projectIdParam,
        inspectionTemplateIdParam,
        checklistItemIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableTemplate} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspectionTemplate.checklistItem(
        projectIdParam,
        inspectionTemplateIdParam,
        checklistItemIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableTemplate} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspectionTemplate.document.onTemplate(
        projectIdParam,
        inspectionTemplateIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableTemplate} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspectionTemplate.document.onSection(
        projectIdParam,
        inspectionTemplateIdParam,
        sectionIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableTemplate} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspectionTemplate.section(
        projectIdParam,
        inspectionTemplateIdParam,
        sectionIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableTemplate} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspectionTemplate.single(
        projectIdParam,
        inspectionTemplateIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableTemplate} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspectionTemplate.root(projectIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableTemplate} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.documentation.dir(projectIdParam, directoryIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableDocumentations} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.documentation.file(projectIdParam, fileIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableDocumentations} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.documentation.root(projectIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableDocumentations} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.edit(projectIdParam, inspectionIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.templateInInspection(
        projectIdParam,
        inspectionIdParam,
        inspectionTemplateIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    // Support for PDF links with /template/ in path (templateId is optional but may be present)
    // Document from template displayed in inspection context
    path: toNestedProjectPath(
      APP_PATHS.inspection.document.onTemplateInInspection(
        projectIdParam,
        inspectionIdParam,
        inspectionTemplateIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.document.onInspection(
        projectIdParam,
        inspectionIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.protocolItem(
        projectIdParam,
        inspectionIdParam,
        protocolIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.complianceCheck(
        projectIdParam,
        inspectionIdParam,
        protocolIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.document.onComplianceCheck(
        projectIdParam,
        inspectionIdParam,
        protocolIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.document.onProtocol(
        projectIdParam,
        inspectionIdParam,
        protocolIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.document.onTemplateSectionInInspection(
        projectIdParam,
        inspectionIdParam,
        inspectionTemplateIdParam,
        sectionIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.document.onSection(
        projectIdParam,
        inspectionIdParam,
        sectionIdParam,
        documentIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.templateSection(
        projectIdParam,
        inspectionIdParam,
        inspectionTemplateIdParam,
        sectionIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.section(
        projectIdParam,
        inspectionIdParam,
        sectionIdParam
      )
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.inspection.single(projectIdParam, inspectionIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableInspection} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(
      APP_PATHS.project.settings.workflowCreator(projectIdParam, workflowIdParam)
    ),
    element: (
      <ProjectAuthWrapper>
        <PrivateRoute element={AuthorizableWorkflowEditor} />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: toNestedProjectPath(APP_PATHS.gdpr(projectIdParam)),
    element: (
      <ProjectAuthWrapper>
        <GDRPInfo />
      </ProjectAuthWrapper>
    ),
  },
  {
    path: '*',
    element: (
      <ProjectAuthWrapper>
        <NotFoundPage />
      </ProjectAuthWrapper>
    ),
  },
];

const ProjectRoutes = (): React.ReactElement => {
  const projectId = useProjectId();
  const { _id: currentUserId, permissions } = useCurrentUser();

  if (
    projectId &&
    currentUserId &&
    hasVisibleNoAccessRoleForProject(permissions, projectId)
  ) {
    return <InsufficientPermissionsPage />;
  }

  return <Outlet />;
};

export default ProjectRoutes;
